import { t } from '@/i18n';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import type {
  ActivityFlowEdgeModel,
  ActivityFlowNodeModel,
} from '../../projects/_models/activity';

type Node = z.infer<typeof ActivityFlowNodeModel>;
type Edge = z.infer<typeof ActivityFlowEdgeModel>;

type InputData = {
  nodes: Node[];
  edges: Edge[];
};

export type PathSegment = {
  fromEdgeId: string;
  nodeId: string;
};

type Route = {
  paths: PathSegment[];
};

type OutputData = {
  originNodeId: string;
  routes: Route[];
};

// 1. target_idの対象となっていないnodeを抜き出す関数
const getOriginNodes = (data: InputData): Node[] => {
  // target_idとして使用されているnodeのIDを集める
  const targetIds = data.edges.map((edge) => edge.targetNodeId);
  // target_idとして使用されていないnodeを返す
  return data.nodes.filter((node) => !targetIds.includes(node.id));
};

// 2. 指定したnodeをsource_idとしているedgesを取得する関数
const getEdgesFromNode = (node: Node, { edges }: InputData): Edge[] => {
  return edges.filter((edge) => edge.sourceNodeId === node.id);
};

// 3. 経路の探索を行い、過不足のないように変換する関数
const exploreRoutesFromNode = (node: Node, data: InputData): Route[] => {
  const routes: Route[] = [];
  const edges = getEdgesFromNode(node, data);

  for (const edge of edges) {
    const nextNode = data.nodes.find((nd) => nd.id === edge.targetNodeId);

    invariant(nextNode, t('unreachable-code'));

    const nextRoutes = exploreRoutesFromNode(nextNode, data);

    if (nextRoutes.length === 0) {
      routes.push({ paths: [{ fromEdgeId: edge.id, nodeId: nextNode.id }] });
    } else {
      for (const route of nextRoutes) {
        routes.push({
          paths: [{ fromEdgeId: edge.id, nodeId: nextNode.id }, ...route.paths],
        });
      }
    }
  }

  return routes;
};

// 上記の関数を使用して、指定された形式にデータを変換する関数
export const transformFlowData = (data: InputData): OutputData[] => {
  const originNodes = getOriginNodes(data);
  return originNodes.map((node) => ({
    originNodeId: node.id,
    routes: exploreRoutesFromNode(node, data),
  }));
};
